import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { meetingsApi } from '../lib/api-client';
import { useToast, getErrorMessage } from '../hooks/use-toast';
import { ToastContainer } from '../components/toast';

interface Meeting {
  id: string;
  title: string;
  description?: string;
  scheduledAt: string;
  endedAt?: string;
  status: 'scheduled' | 'live' | 'ended' | 'cancelled';
  serverStatus?: 'deallocated' | 'starting' | 'ready' | 'stopping';
  livekitRoomId?: string;
  recordingUrl?: string;
  createdBy: {
    firstName: string;
    lastName: string;
  };
  _count?: {
    qa: number;
  };
}

interface Question {
  id: string;
  question: string;
  answer?: string;
  status: 'pending' | 'answered' | 'hidden';
  member: {
    firstName: string;
    lastName: string;
  };
  answeredBy?: {
    firstName: string;
    lastName: string;
  };
  createdAt: string;
}

export default function MeetingsPage() {
  const [pendingAction, setPendingAction] = useState<{
    message: string;
    run: () => Promise<void>;
  } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [showQAModal, setShowQAModal] = useState(false);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [showAnswerModal, setShowAnswerModal] = useState(false);
  const [answeringQuestionId, setAnsweringQuestionId] = useState<string | null>(null);
  const [answerText, setAnswerText] = useState('');

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');

  // Toast notifications
  const { toasts, showToast, removeToast } = useToast();

  useEffect(() => {
    loadMeetings();
  }, [filter]);

  const loadMeetings = async () => {
    try {
      setLoading(true);
      const data = await meetingsApi.getMeetings(filter || undefined);
      setMeetings(data);
    } catch (error) {
      showToast(getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMeeting = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await meetingsApi.createMeeting(title, description, scheduledAt);
      setShowCreateModal(false);
      setTitle('');
      setDescription('');
      setScheduledAt('');
      loadMeetings();
      showToast('Meeting created successfully', 'success');
    } catch (error) {
      console.error('Failed to create meeting:', error);
      showToast(getErrorMessage(error), 'error');
    }
  };

  const handleStartMeeting = async (id: string) => {
    try {
      await meetingsApi.startMeeting(id);
      loadMeetings();
      showToast('Meeting started successfully', 'success');
    } catch (error) {
      console.error('Failed to start meeting:', error);
      showToast(getErrorMessage(error), 'error');
    }
  };

  const handleEndMeeting = async (id: string) => {
    try {
      await meetingsApi.endMeeting(id);
      loadMeetings();
      showToast('Meeting ended successfully', 'success');
    } catch (error) {
      console.error('Failed to end meeting:', error);
      showToast(getErrorMessage(error), 'error');
    }
  };

  const handleDeleteMeeting = async (id: string) => {
    try {
      await meetingsApi.deleteMeeting(id);
      loadMeetings();
      showToast('Meeting deleted successfully', 'success');
    } catch (error) {
      console.error('Failed to delete meeting:', error);
      showToast(getErrorMessage(error), 'error');
    }
  };

  const handleViewQA = async (meeting: Meeting) => {
    setSelectedMeeting(meeting);
    try {
      const data = await meetingsApi.getQuestions(meeting.id);
      setQuestions(data);
      setShowQAModal(true);
    } catch (error) {
      console.error('Failed to load questions:', error);
      showToast(getErrorMessage(error), 'error');
    }
  };

  const handleAnswerQuestion = async (questionId: string) => {
    setAnsweringQuestionId(questionId);
    setShowAnswerModal(true);
  };

  const submitAnswer = async () => {
    if (!answerText.trim() || !answeringQuestionId) return;
    try {
      await meetingsApi.answerQuestion(answeringQuestionId, answerText);
      if (selectedMeeting) {
        const data = await meetingsApi.getQuestions(selectedMeeting.id);
        setQuestions(data);
      }
      setShowAnswerModal(false);
      setAnswerText('');
      setAnsweringQuestionId(null);
      showToast('Answer posted successfully', 'success');
    } catch (error) {
      console.error('Failed to answer question:', error);
      showToast(getErrorMessage(error), 'error');
    }
  };

  const handleHideQuestion = async (questionId: string) => {
    if (!confirm('Hide this question?')) return;
    try {
      await meetingsApi.hideQuestion(questionId);
      if (selectedMeeting) {
        const data = await meetingsApi.getQuestions(selectedMeeting.id);
        setQuestions(data);
      }
      showToast('Question hidden successfully', 'success');
    } catch (error) {
      console.error('Failed to hide question:', error);
      showToast(getErrorMessage(error), 'error');
    }
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(dateString));
  };

  const getStatusBadge = (status: Meeting['status']) => {
    const variants = {
      scheduled: 'bg-[#e1e5eb] text-[#4b607f] border-[#d1d8e3]',
      live: 'bg-[#fff1e6] text-[#f3701e] border-[#f0d8c1]',
      ended: 'bg-[#f6f6f3] text-[#68716d] border-[#e5e4df]',
      cancelled: 'bg-[#fbe9e6] text-[#c5523f] border-[#f0c8c1]',
    };
    return (
      <span
        className={`px-[11px] py-[5px] rounded-full text-[11px] font-medium border capitalize ${variants[status]}`}
      >
        {status}
      </span>
    );
  };

  return (
    <div className="max-w-[1100px]">
      {/* Page Header */}
      <div className="mb-8">
        <span className="block text-[10px] tracking-[1.2px] text-[#68716d] mb-[9px] uppercase font-semibold">
          Opaskwayak Cree Nation
        </span>
        <h1 className="text-[32px] font-semibold text-[#424a47] leading-[1.2] tracking-[-0.5px] mb-[9px]">
          Meetings management
        </h1>
        <p className="text-[14px] text-[#68716d] leading-[1.6] mb-6">
          Plan and manage your community conversations.
        </p>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-[10px] bg-[#242728] text-white rounded-[9px] text-[13px] font-semibold hover:bg-[#3c413d] active:scale-[0.98] transition-all inline-flex items-center gap-2"
        >
          <svg
            className="w-[16px] h-[16px]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.65}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          Create meeting
        </button>
      </div>

      {/* Filter Tabs Card */}
      <div className="bg-white rounded-[13px] shadow-[0_2px_8px_rgba(0,0,0,0.06)] overflow-hidden mb-6">
        <div className="flex gap-1 px-4 border-b border-[#e5e4df]">
          {[
            { value: '', label: 'All' },
            { value: 'scheduled', label: 'Scheduled' },
            { value: 'live', label: 'Live' },
            { value: 'ended', label: 'Ended' },
          ].map((tab) => (
            <button
              key={tab.value}
              onClick={() => setFilter(tab.value)}
              className={`px-4 py-[11px] font-medium text-[13px] transition-colors relative ${
                filter === tab.value ? 'text-[#424a47]' : 'text-[#68716d] hover:text-[#424a47]'
              }`}
            >
              {tab.label}
              {filter === tab.value && (
                <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#4b607f]" />
              )}
            </button>
          ))}
        </div>

        {/* Meetings List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-12 h-12 border-4 border-[#4b607f]/20 border-t-[#4b607f] rounded-full animate-spin" />
          </div>
        ) : meetings.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-[64px] h-[64px] mx-auto mb-4 rounded-full bg-[#f6f6f3] flex items-center justify-center">
              <svg
                className="w-[32px] h-[32px] text-[#68716d]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.65}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h3 className="text-[18px] font-semibold text-[#424a47] mb-2">
              {filter ? `No ${filter} meetings` : 'No meetings scheduled'}
            </h3>
            <p className="text-[14px] text-[#68716d] mb-6">
              {filter
                ? 'Try another filter or create a new meeting'
                : 'Add a meeting and give members a place to connect'}
            </p>
            {!filter && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-[11px] bg-[#242728] text-white rounded-[9px] text-[13px] font-semibold hover:bg-[#3c413d] active:scale-[0.98] transition-all inline-flex items-center gap-2"
              >
                <svg
                  className="w-[16px] h-[16px]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.65}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                Schedule a meeting
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#f6f6f3] border-b border-[#e5e4df]">
                  <tr>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-[#68716d] uppercase tracking-[0.5px]">
                      Title
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-[#68716d] uppercase tracking-[0.5px]">
                      Scheduled
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-[#68716d] uppercase tracking-[0.5px]">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-[#68716d] uppercase tracking-[0.5px]">
                      Q&A
                    </th>
                    <th className="px-4 py-3 text-right text-[11px] font-medium text-[#68716d] uppercase tracking-[0.5px]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e5e4df]">
                  {meetings.map((meeting) => (
                    <tr key={meeting.id} className="hover:bg-[#f6f6f3] transition-colors">
                      <td className="px-4 py-4">
                        <div className="font-medium text-[#424a47] text-[14px]">
                          {meeting.title}
                        </div>
                        {meeting.description && (
                          <div className="text-[12px] text-[#68716d] truncate max-w-xs mt-1">
                            {meeting.description}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-[13px] text-[#68716d] whitespace-nowrap">
                        {formatDate(meeting.scheduledAt)}
                      </td>
                      <td className="px-4 py-4">{getStatusBadge(meeting.status)}</td>
                      <td className="px-4 py-4 text-[13px] text-[#424a47]">
                        {meeting._count?.qa || 0}{' '}
                        {meeting._count?.qa === 1 ? 'question' : 'questions'}
                        {meeting._count && meeting._count.qa > 0 && (
                          <button
                            onClick={() => handleViewQA(meeting)}
                            className="ml-2 text-[#4b607f] hover:underline text-[12px] font-medium"
                          >
                            View
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-4 text-right text-[13px] font-medium space-x-2 whitespace-nowrap">
                        {meeting.status === 'scheduled' && (
                          <button
                            onClick={() =>
                              setPendingAction({
                                message: 'Start this meeting?',
                                run: () => handleStartMeeting(meeting.id),
                              })
                            }
                            className="text-[#4b607f] hover:underline font-medium"
                          >
                            Start
                          </button>
                        )}
                        {meeting.status === 'live' && (
                          <Link
                            to={`/meetings/${meeting.id}/room`}
                            className="text-[#4b607f] hover:underline font-medium"
                          >
                            Join as admin
                          </Link>
                        )}
                        {meeting.status === 'live' && (
                          <button
                            onClick={() =>
                              setPendingAction({
                                message: 'End this meeting for everyone?',
                                run: () => handleEndMeeting(meeting.id),
                              })
                            }
                            className="text-[#f3701e] hover:underline font-medium"
                          >
                            End
                          </button>
                        )}
                        <button
                          onClick={() => handleViewQA(meeting)}
                          className="text-[#4b607f] hover:underline font-medium"
                        >
                          Q&A
                        </button>
                        {meeting.status !== 'live' && (
                          <button
                            onClick={() =>
                              setPendingAction({
                                message: 'Delete this meeting permanently?',
                                run: () => handleDeleteMeeting(meeting.id),
                              })
                            }
                            className="text-[#c5523f] hover:underline font-medium"
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Table Footer */}
            <div className="px-4 py-4 border-t border-[#e5e4df]">
              <span className="text-[13px] text-[#68716d]">
                {meetings.length} {meetings.length === 1 ? 'meeting' : 'meetings'}
              </span>
            </div>
          </>
        )}
      </div>

      {pendingAction && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="meeting-action-title"
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
        >
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 id="meeting-action-title" className="text-lg font-semibold mb-5">
              {pendingAction.message}
            </h2>
            <button
              disabled={actionBusy}
              onClick={async () => {
                setActionBusy(true);
                try {
                  await pendingAction.run();
                  setPendingAction(null);
                } finally {
                  setActionBusy(false);
                }
              }}
              className="bg-[#242728] text-white rounded px-4 py-2 mr-3"
            >
              {actionBusy ? 'Working...' : 'Confirm'}
            </button>
            <button
              disabled={actionBusy}
              onClick={() => setPendingAction(null)}
              className="border rounded px-4 py-2"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {/* Create Meeting Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={() => setShowCreateModal(false)}
        >
          <div
            className="bg-white rounded-card shadow-card p-8 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-charcoal mb-1">Create Meeting</h2>
              <p className="text-sm text-muted">Schedule a new community meeting</p>
            </div>
            <form onSubmit={handleCreateMeeting} className="space-y-5">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-charcoal mb-2">
                  Title <span className="text-orange">*</span>
                </label>
                <input
                  type="text"
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-4 py-2.5 text-base border border-line rounded-input bg-white text-charcoal placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-slate focus:border-transparent transition-all"
                  placeholder="Community Town Hall"
                  required
                />
              </div>
              <div>
                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-charcoal mb-2"
                >
                  Description
                </label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-4 py-2.5 text-base border border-line rounded-input bg-white text-charcoal placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-slate focus:border-transparent transition-all resize-y"
                  rows={3}
                  placeholder="Brief description of the meeting"
                />
              </div>
              <div>
                <label
                  htmlFor="scheduledAt"
                  className="block text-sm font-medium text-charcoal mb-2"
                >
                  Scheduled Date & Time <span className="text-orange">*</span>
                </label>
                <input
                  type="datetime-local"
                  id="scheduledAt"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  className="w-full px-4 py-2.5 text-base border border-line rounded-input bg-white text-charcoal focus:outline-none focus:ring-2 focus:ring-slate focus:border-transparent transition-all"
                  required
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  className="flex-1 bg-charcoal text-white py-3 rounded-button text-sm font-semibold hover:bg-charcoal/90 active:scale-[0.98] transition-all shadow-button"
                >
                  Create Meeting
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-6 py-3 border border-line rounded-button hover:bg-workspace transition-colors text-sm font-medium text-charcoal"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Q&A Modal */}
      {showQAModal && selectedMeeting && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={() => setShowQAModal(false)}
        >
          <div
            className="bg-white rounded-card shadow-card p-8 w-full max-w-3xl max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-semibold text-charcoal mb-1">
                  {selectedMeeting.title}
                </h2>
                <p className="text-sm text-muted">Q&A Management</p>
              </div>
              <button
                onClick={() => setShowQAModal(false)}
                className="text-muted hover:text-charcoal text-2xl font-light leading-none"
              >
                ×
              </button>
            </div>

            {questions.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-workspace flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-muted"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.65}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                </div>
                <p className="text-muted text-sm">No questions yet for this meeting</p>
              </div>
            ) : (
              <div className="space-y-4">
                {questions.map((qa) => (
                  <div
                    key={qa.id}
                    className={`border rounded-card p-5 transition-all ${
                      qa.status === 'hidden'
                        ? 'bg-clay/10 border-clay/20 opacity-60'
                        : qa.status === 'answered'
                          ? 'bg-slate/5 border-slate/20'
                          : 'bg-white border-line'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <p className="font-medium text-charcoal leading-relaxed">{qa.question}</p>
                        <p className="text-xs text-muted mt-1.5">
                          Asked by {qa.member.firstName} {qa.member.lastName}
                        </p>
                      </div>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium border ml-3 whitespace-nowrap ${
                          qa.status === 'answered'
                            ? 'bg-slate/10 text-slate border-slate/20'
                            : qa.status === 'hidden'
                              ? 'bg-clay/10 text-clay border-clay/20'
                              : 'bg-orange/10 text-orange border-orange/20'
                        }`}
                      >
                        {qa.status}
                      </span>
                    </div>

                    {qa.answer && (
                      <div className="mt-3 ml-4 pl-4 border-l-2 border-slate">
                        <p className="text-charcoal leading-relaxed">{qa.answer}</p>
                        {qa.answeredBy && (
                          <p className="text-xs text-muted mt-1.5">
                            Answered by {qa.answeredBy.firstName} {qa.answeredBy.lastName}
                          </p>
                        )}
                      </div>
                    )}

                    <div className="mt-4 flex gap-2">
                      {qa.status !== 'answered' && qa.status !== 'hidden' && (
                        <button
                          onClick={() => handleAnswerQuestion(qa.id)}
                          className="text-sm bg-slate text-white px-4 py-2 rounded-button font-medium hover:bg-slate/90 active:scale-[0.98] transition-all"
                        >
                          Answer
                        </button>
                      )}
                      {qa.status !== 'hidden' && (
                        <button
                          onClick={() => handleHideQuestion(qa.id)}
                          className="text-sm border border-line px-4 py-2 rounded-button font-medium hover:bg-workspace transition-colors text-charcoal"
                        >
                          Hide
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-8 pt-6 border-t border-line">
              <button
                onClick={() => setShowQAModal(false)}
                className="w-full px-4 py-3 bg-charcoal text-white rounded-button text-sm font-semibold hover:bg-charcoal/90 active:scale-[0.98] transition-all shadow-button"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Answer Modal */}
      {showAnswerModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4"
          onClick={() => {
            setShowAnswerModal(false);
            setAnswerText('');
            setAnsweringQuestionId(null);
          }}
        >
          <div
            className="bg-white rounded-card shadow-card p-8 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-charcoal mb-1">Answer Question</h2>
              <p className="text-sm text-muted">Provide your answer to this question</p>
            </div>
            <div className="space-y-5">
              <div>
                <label htmlFor="answer" className="block text-sm font-medium text-charcoal mb-2">
                  Your Answer <span className="text-orange">*</span>
                </label>
                <textarea
                  id="answer"
                  value={answerText}
                  onChange={(e) => setAnswerText(e.target.value)}
                  className="w-full px-4 py-2.5 text-base border border-line rounded-input bg-white text-charcoal placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-slate focus:border-transparent transition-all resize-y"
                  rows={4}
                  placeholder="Enter your detailed answer here..."
                  required
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={submitAnswer}
                  disabled={!answerText.trim()}
                  className="flex-1 bg-charcoal text-white py-3 rounded-button text-sm font-semibold hover:bg-charcoal/90 active:scale-[0.98] transition-all shadow-button disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Submit Answer
                </button>
                <button
                  onClick={() => {
                    setShowAnswerModal(false);
                    setAnswerText('');
                    setAnsweringQuestionId(null);
                  }}
                  className="px-6 py-3 border border-line rounded-button hover:bg-workspace transition-colors text-sm font-medium text-charcoal"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}
